import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { format, subMonths } from 'date-fns';
import { cs } from 'date-fns/locale';
import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../paths';
import { formatPrice } from '../../utils/formatPrice';
import { DealerOnly, dashboardCardSx } from '../common';
import { useAuth } from '../hooks';

type Period = 'previous' | 'current';

const PERIODS: Period[] = ['previous', 'current'];

const periodKeys = (date: Date) => ({
  year: String(date.getFullYear()),
  month: String(date.getMonth() + 1).padStart(2, '0')
});

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

interface PeriodSwitchProps {
  value: Period;
  labels: Record<Period, string>;
  missing: Record<Period, boolean>;
  onChange: (period: Period) => void;
}

/** Segmented control: one pill per month, a warning icon marks months without fuel data. */
const PeriodSwitch = ({ value, labels, missing, onChange }: PeriodSwitchProps) => (
  <Box
    role="tablist"
    aria-label="Období"
    sx={{
      display: 'inline-grid',
      gridAutoFlow: 'column',
      gridAutoColumns: '1fr',
      gap: '4px',
      padding: '4px',
      borderRadius: 2.5,
      backgroundColor: 'rgba(34, 34, 52, 0.05)',
      alignSelf: { xs: 'stretch', md: 'auto' }
    }}
  >
    {PERIODS.map((period) => {
      const selected = value === period;
      return (
        <Box
          key={period}
          component="button"
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(period)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            whiteSpace: 'nowrap',
            border: 0,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '0.9rem',
            fontWeight: selected ? 600 : 500,
            color: selected ? 'text.primary' : 'text.secondary',
            paddingY: 1,
            paddingX: 2.5,
            borderRadius: 2,
            backgroundColor: selected ? 'background.paper' : 'transparent',
            boxShadow: selected ? '0 1px 2px rgba(34, 34, 52, 0.12), 0 2px 8px -4px rgba(34, 34, 52, 0.2)' : 'none',
            '&:hover': { color: 'text.primary' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
            '@media (prefers-reduced-motion: no-preference)': {
              transition: 'background-color 0.2s, color 0.2s, box-shadow 0.2s'
            }
          }}
        >
          {labels[period]}
          {missing[period] && (
            <Tooltip title="Chybí údaje o pohonných hmotách">
              <ErrorOutlineIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
      );
    })}
  </Box>
);

interface FuelMetricProps {
  label: string;
  value: ReactNode;
  unit?: string;
}

const FuelMetric = ({ label, value, unit }: FuelMetricProps) => (
  <Stack spacing={1} sx={{ minWidth: 0 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="baseline" gap={0.75}>
      <Typography
        sx={{
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {value}
      </Typography>
      {unit && (
        <Typography color="text.secondary" fontWeight={500}>
          {unit}
        </Typography>
      )}
    </Stack>
  </Stack>
);

/** Read-only summary. Values are entered on the statements page, not on the dashboard. */
const FuelWidgetConnectedContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const now = new Date();
  const dates: Record<Period, Date> = { previous: subMonths(now, 1), current: now };
  const saved = (period: Period) => {
    const { year, month } = periodKeys(dates[period]);
    return { price: user?.avgPhmPrices?.[year]?.[month], liters: user?.phmLiters?.[year]?.[month] };
  };
  const missing: Record<Period, boolean> = {
    previous: saved('previous').price == null || saved('previous').liters == null,
    current: saved('current').price == null || saved('current').liters == null
  };
  const labels: Record<Period, string> = {
    previous: capitalize(format(dates.previous, 'LLLL yyyy', { locale: cs })),
    current: capitalize(format(dates.current, 'LLLL yyyy', { locale: cs }))
  };

  const [period, setPeriod] = useState<Period>(() => (missing.previous ? 'previous' : 'current'));
  const { price, liters } = saved(period);
  const totalCost = price != null && liters != null ? price * liters : null;
  const goToStatements = () => navigate(PATHS.STATEMENTS);

  return (
    <Box
      sx={{
        ...dashboardCardSx,
        height: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: { xs: 2, md: 3 }
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 40,
              height: 40,
              borderRadius: 2,
              color: 'primary.main',
              backgroundColor: 'rgba(198, 35, 39, 0.08)'
            }}
          >
            <LocalGasStationOutlinedIcon />
          </Box>
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              Pohonné hmoty
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Průměrná cena a natankované litry
            </Typography>
          </Stack>
        </Stack>
        <PeriodSwitch value={period} labels={labels} missing={missing} onChange={setPeriod} />
      </Stack>

      {missing[period] && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          gap={1}
          sx={{ borderRadius: 2, backgroundColor: 'rgba(252, 163, 17, 0.14)', padding: 1.5, paddingLeft: 2 }}
        >
          <Typography variant="body2" fontWeight={500}>
            Za {labels[period]} chybí údaje o pohonných hmotách. Doplníte je ve výkazu obchodníka.
          </Typography>
          <Button
            size="small"
            color="inherit"
            endIcon={<ArrowForwardIcon />}
            onClick={goToStatements}
            sx={{ fontWeight: 600, flexShrink: 0 }}
          >
            Otevřít výkaz
          </Button>
        </Stack>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'flex-end' }}
        justifyContent="space-between"
        gap={3}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(120px, 1fr))' },
            columnGap: { xs: 3, md: 6 },
            rowGap: 2
          }}
        >
          <FuelMetric
            label="Průměrná cena"
            value={price != null ? price : '-'}
            unit={price != null ? 'Kč/l' : undefined}
          />
          <FuelMetric label="Litry" value={liters != null ? liters : '-'} unit={liters != null ? 'l' : undefined} />
          <FuelMetric label="Náklady celkem" value={totalCost != null ? formatPrice(totalCost) : '-'} />
        </Box>
        {!missing[period] && (
          <Button color="primary" endIcon={<ArrowForwardIcon />} onClick={goToStatements} sx={{ flexShrink: 0 }}>
            Otevřít výkaz
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export const FuelWidgetConnected = () => (
  <DealerOnly>
    <FuelWidgetConnectedContent />
  </DealerOnly>
);
